import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", sub: "Reinventing the month-end close", quote: "\u201CAgents reconcile millions of transactions across 12 ERPs and surface only the exceptions that need a human.\u201D", m: [["45%", "Faster Close"], ["12", "ERPs Unified"]], tags: ["Reconciliation", "Finance Agents", "ERP"] },
  { company: "Fortune 100 Bank", sub: "Trusted context for finance ops", quote: "\u201CA shared semantic layer means every team and agent finally works from the same governed numbers.\u201D", m: [["3\u00D7", "Faster Reconciliation"], ["100%", "Governed"]], tags: ["Semantic Layer", "Governance", "BFSI"] },
  { company: "Industrial Manufacturer", sub: "Smarter demand & supply planning", quote: "\u201CAgentic planning balances service levels and working capital across a global supply network in real time.\u201D", m: [["20%", "Lower Inventory"], ["Live", "In Production"]], tags: ["Demand Planning", "Inventory"] },
  { company: "Global Retail Group", sub: "Campaign orchestration at scale", quote: "\u201CPlanning, content and activation are coordinated end-to-end \u2014 what took weeks now happens in hours.\u201D", m: [["2\u00D7", "Campaign Throughput"], ["Live", "Across Channels"]], tags: ["Marketing", "Content Ops"] },
];

const Card = ({ c, dark }) => (
  <div className={`shrink-0 w-[300px] sm:w-[340px] snap-start rounded-2xl p-6 flex flex-col ${dark ? "bg-gradient-to-b from-[#2E1B0C] to-[#160D05] text-[#F7F5F0]" : "bg-white border border-[#3E2818]/10 text-[#3E2818]"}`}>
    <h4 className={`font-display text-2xl font-medium tracking-tight ${dark ? "text-white" : "text-[#3E2818]"}`}>{c.company}</h4>
    <p className={`text-[12.5px] mt-1 ${dark ? "text-white/60" : "text-[#3E2818]/55"}`}>{c.sub}</p>
    <div className={`h-px my-4 ${dark ? "bg-white/15" : "bg-[#3E2818]/10"}`} />
    <p className={`text-[13.5px] leading-relaxed italic ${dark ? "text-white/85" : "text-[#3E2818]/75"}`}>{c.quote}</p>
    <div className="mt-6 flex gap-8">
      {c.m.map(([n, l]) => (
        <div key={l}>
          <div className={`font-display text-2xl font-medium ${dark ? "text-[#E8AA68]" : "text-[#8A5730]"}`}>{n}</div>
          <div className={`text-[10.5px] uppercase tracking-[0.12em] mt-0.5 ${dark ? "text-white/55" : "text-[#3E2818]/50"}`}>{l}</div>
        </div>
      ))}
    </div>
    <div className="mt-5 flex flex-wrap gap-2">
      {c.tags.map((t) => (
        <span key={t} className={`text-[11px] font-semibold rounded-md px-2.5 py-1 ${dark ? "bg-[#8A5730]/40 text-[#F0D9BE]" : "bg-[#8A5730]/10 text-[#8A5730]"}`}>{t}</span>
      ))}
    </div>
  </div>
);

const Carousel = ({ variant }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Powering Enterprises of All Sizes</p>
          <h2 className="font-display text-3xl lg:text-[2.75rem] font-medium tracking-tight text-[#3E2818] leading-[1.05]">Agents <span className="italic">in production</span><br />not just demos.</h2>
          <p className="text-sm text-[#3E2818]/60 mt-4 max-w-md leading-relaxed">Real deployments. Measurable outcomes. Enterprises across finance, supply chain, marketing and service run agents at scale with Intugle.</p>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowLeft size={17} /></button>
          <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowRight size={17} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CASES.map((c, i) => <Card key={c.company} c={c} dark={variant === "a" && i === 0} />)}
        {CASES.map((c, i) => <Card key={c.company + "2"} c={c} dark={false} />)}
      </div>
    </div>
  );
};

const OPTIONS = [
  { id: "a", title: "Option A · Faithful (one dark highlight card)", note: "Matches your reference: horizontal carousel, serif headline with italic accent, prev/next arrows, and the first card highlighted in dark espresso. Quote + two metrics + tag pills per card.", variant: "a" },
  { id: "b", title: "Option B · Uniform Cream Cards", note: "Same carousel and card anatomy, but every card is a clean cream/white card (no dark highlight). Calmer and more uniform.", variant: "b" },
];

export default function CaseStudyOptions3() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · Carousel Templates</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Carousel style (like your reference)</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Placeholder stories. Drag/scroll or use the arrows. Scales to 8&ndash;10 cards. Tell me the letter.</p>

        <div className="mt-12 space-y-20">
          {OPTIONS.map((o) => (
            <div key={o.id}>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-6">{o.title}</p>
              <Carousel variant={o.variant} />
              <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
